/**
 * Spline entity → `RenderableEntity` adapter (TYPED).
 *
 * DEFUSE Direction-3 — typed sibling of `../splineToRenderable.ts`. Reads the
 * canonical `SplineEntity` fields directly off the model (`model.ts`:
 * `splineEntitySchema` — `controlPoints` / `knots` / `degree` /
 * `startTangent` / `endTangent`) instead of the legacy
 * `EntityPreview.properties` string bag. Produces a byte-identical
 * `RenderableEntity` (golden-parity) for the same entity.
 *
 * # CLAUDE.md priority #1 — cubic Bezier only
 *   `tangentVectors` (DXF group codes 12 / 13) are preserved EXPLICITLY,
 *   never quadratic, never approximated to a straight polyline. The shared
 *   builder shapes them onto `geometry.tangentVectors`.
 *
 * Golden-parity notes — the typed model carries MORE than the legacy bag:
 *   - CONTROL-POINT z: the legacy producer `buildSplinePreview`
 *     (`dxfGeometry.ts`) serializes each control point as `"x,y"` — it DROPS
 *     z — so the legacy `splineToRenderable` reads `parts[2] === undefined`
 *     → z = 0. The typed model's `controlPoints` are `drawingPoint3DSchema`
 *     (real z, default 0). This adapter reads `cp.z ?? 0`, so a real model z
 *     surfaces in the typed geometry — MORE faithful, not a divergence bug.
 *     The parity test compares apples-to-apples with a z = 0 fixture and
 *     asserts the richer z as a documented superset.
 *   - TANGENT z: the legacy bag writes only `Start/End tangent X` + `Y` (no
 *     Z key), so legacy tangent z is always 0. The typed model's
 *     `startTangent` / `endTangent` are `drawingPoint3DSchema` (real z). Same
 *     superset treatment.
 *   - Tangent vectors are raw DELTAS (relative to the first / last fit point),
 *     NOT world points → the builder does NOT run them through `applyOffset`
 *     (matching the legacy sibling). Only present when BOTH start AND end
 *     tangents exist (identical to the legacy `&& !== null` gate).
 *   - DEGREE: the legacy adapter clamps `Math.max(1, Math.round(degree))`.
 *     The model schema is `int().positive()`, so this is a no-op for valid
 *     data — but we apply the IDENTICAL clamp so any future degenerate value
 *     maps the same way both paths.
 *
 * Category B — standalone file; NOT imported into any render path yet (the
 * typed dispatcher does not route Spline until a later phase).
 */

import { fitPointsToControlPoints } from "@dxf-ai-editor/drawing-model";
import type { SplineEntity } from "@dxf-ai-editor/drawing-model";

import type { RenderableEntity, Vec3 } from "../../types";

import { buildRenderableShell, type AdapterContext } from "../adapterContext";
import { buildSplineSceneGeometry } from "../geom/splineGeometry";

/** Typed adapter for the `Spline` entity (`splineEntitySchema`). */
export function splineFromEntity(
  entity: SplineEntity,
  ctx: AdapterContext,
): RenderableEntity {
  // Identical clamp to the legacy adapter (no-op for the schema's positive
  // int degree, but keeps the math byte-identical for any future value).
  const degree = Math.max(1, Math.round(entity.degree));

  // ── FIT-POINT mode (@codex #431 round-2) ────────────────────────────
  // A valid DXF SPLINE may be authored in fit-points mode (`fitPoints`
  // populated, `controlPoints` empty — DXF 74 > 0, 73 = 0). The scene
  // geometry is control-point NURBS, so without conversion such a spline
  // rendered as an EMPTY curve with a degenerate bbox — on the TOP-LEVEL
  // typed path AND for block children (the legacy bag path has the same
  // hole: `splineToRenderable` never reads fit points, §3.4#4 — so this is
  // a documented-superset fix at the SHARED adapter seam, not per-site).
  // Reuse the one-truth interpolator `fitPointsToControlPoints`
  // (drawing-model `splineMath`, same call the property translator makes):
  // it consumes the optional start/end tangents as boundary conditions, so
  // the derived control points already encode them — no separate
  // `tangentVectors` is emitted for this mode (passing them again would
  // double-apply the constraint downstream).
  const fitMode =
    entity.controlPoints.length === 0 && entity.fitPoints.length > 0;

  const source = fitMode
    ? fitPointsToControlPoints(
        entity.fitPoints,
        entity.startTangent,
        entity.endTangent,
        degree,
      )
    : { controlPoints: entity.controlPoints, knots: entity.knots };

  // Control points are absolute world positions; the shared builder applies
  // `applyOffset`. The model carries a real z (the legacy bag dropped it →
  // 0); pass it through — the parity test documents this superset.
  const controlPointsRaw: Vec3[] = source.controlPoints.map((cp) => ({
    x: cp.x,
    y: cp.y,
    z: cp.z ?? 0,
  }));

  // Tangents only when BOTH are present (mirrors the legacy `&& !== null`
  // gate). Raw deltas → NOT offset (the builder leaves them untouched). The
  // model carries a real tangent z (the legacy bag had no Z key → 0); pass
  // it through as a documented superset. Fit mode consumes the tangents in
  // the interpolation above instead (see the fit-point note).
  const startTangent = entity.startTangent;
  const endTangent = entity.endTangent;
  const tangentVectors =
    !fitMode && startTangent && endTangent
      ? {
          start: { x: startTangent.x, y: startTangent.y, z: startTangent.z ?? 0 },
          end: { x: endTangent.x, y: endTangent.y, z: endTangent.z ?? 0 },
        }
      : undefined;

  const { geometry, bounds } = buildSplineSceneGeometry(
    controlPointsRaw,
    source.knots,
    degree,
    tangentVectors,
  );

  return buildRenderableShell(entity, ctx, geometry, bounds);
}
